import { Row, Col, Grid, Card, Typography, Divider } from "antd";
import { VehicleTile } from "components";
import { FINANCIALS_TIES_DATA, FINANCIALS_WIDGET } from "../__mock__";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../store";
import { useEffect, useMemo } from "react";
import { parseBillingFieldsTable } from "../../../../utils/parsers/billing-parser";
import { useCookies } from "react-cookie";
import { setTotalBill } from "store/actions/billing-actions";
import {
  DollarOutlined,
  SwapOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";

import "styles/components/tab-content/financials-content/financials-content.scss";

const { useBreakpoint } = Grid;
const { Text, Title } = Typography;

export interface FinancialsTileContainerProps {
  selectedDate?: string;
}

export default function FinancialsTileContainer({
  selectedDate,
}: FinancialsTileContainerProps) {
  const [{ meta }] = useCookies();
  const dispatch = useDispatch();
  const screens = useBreakpoint();

  // Determine screen size
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;

  const { bill, totalBill } = useSelector<AppState, AppState["billing"]>(
    (state) => state.billing
  );

  useEffect(() => {
    if (meta?.user?.organizationId && selectedDate) {
      dispatch({
        organization_id: meta.user.organizationId,
        date: selectedDate,
      });
    }
  }, [dispatch, meta, selectedDate]);

  const { organizationId } = useSelector<AppState, AppState["common"]>(
    (state) => state.common
  );

  const data = useMemo(() => parseBillingFieldsTable(bill), [bill]);

  // Original Billing Components (Desktop)
  interface BillingTileProps {
    title: string;
    value: string;
  }

  interface BillingSectionProps {
    title: string;
    children: React.ReactNode;
  }

  interface BillingRowProps {
    children: React.ReactNode;
  }

  const BillingTile: React.FC<BillingTileProps> = ({ title, value }) => (
    <div className="billing-tile">
      <p>{title}</p>
      <h1>{value}</h1>
    </div>
  );

  const BillingSection: React.FC<BillingSectionProps> = ({ title, children }) => (
    <div className="billing-section">
      <h2>{title}</h2>
      {children}
    </div>
  );

  const BillingRow: React.FC<BillingRowProps> = ({ children }) => (
    <div className="billing-row">{children}</div>
  );

  // Mobile Billing Components
  interface MobileBillingTileProps {
    title: string;
    value: string;
    highlight?: boolean;
  }

  const MobileBillingTile: React.FC<MobileBillingTileProps> = ({
    title,
    value,
    highlight = false,
  }) => (
    <div className={`mobile-billing-tile ${highlight ? "highlight" : ""}`}>
      <Text type="secondary" className="mobile-tile-title">
        {title}
      </Text>
      <Title level={4} className="mobile-tile-value">
        {value}
      </Title>
    </div>
  );

  const MobileBillingSection: React.FC<BillingSectionProps> = ({
    title,
    children,
  }) => (
    <Card className="mobile-billing-section" size="small">
      <div className="mobile-section-header">
        <Title level={5} className="mobile-section-title">
          {title}
        </Title>
      </div>
      <Divider className="mobile-section-divider" />
      <div className="mobile-section-content">{children}</div>
    </Card>
  );

  // Mobile View
  if (isMobile || isTablet) {
    return (
      <div className={`billing-container-mobile ${isTablet ? "tablet" : ""}`}>
        {/* Battery Subscription Cost Section */}
        <MobileBillingSection title="Battery Subscription Cost">
          <div className="mobile-billing-row">
            <MobileBillingTile
              title="Total Distance"
              value={`${data.distance || 0} KM`}
            />
            <MobileBillingTile
              title="Rate Per Km"
              value={data.perKmCost || "—"}
            />
            <MobileBillingTile
              title="Total Subscription Cost"
              value={`PKR ${data.batterySubCharges || 0}`}
              highlight
            />
          </div>
        </MobileBillingSection>

        {/* Battery Swap Cost Section */}
        <MobileBillingSection title="Battery Swap Cost">
          <div className="mobile-billing-row">
            <MobileBillingTile
              title="Total Swaps"
              value={`${data.swaps || 0}`}
            />
            <MobileBillingTile
              title="Per Swap Cost"
              value={data.perSwapCost || "—"}
            />
            <MobileBillingTile
              title="Total Swaps Cost"
              value={`PKR ${data.swapCharges || 0}`}
              highlight
            />
          </div>
        </MobileBillingSection>

        {/* Dynamic Billing Items */}
        {data.billingItems?.map((dynamicItems: any, index: number) => (
          <MobileBillingSection key={index} title={dynamicItems.item}>
            <div className="mobile-billing-row">
              <MobileBillingTile
                title={dynamicItems.units === "KM" ? "Distance" : "Quantity"}
                value={`${dynamicItems.quantity} ${dynamicItems.units}`}
              />
              <MobileBillingTile
                title="Rate"
                value={
                  dynamicItems.rate !== 0 ? `PKR ${dynamicItems.rate}` : "—"
                }
              />
              <MobileBillingTile
                title="Total"
                value={`${dynamicItems.total}`}
                highlight
              />
            </div>
          </MobileBillingSection>
        ))}
      </div>
    );
  }

  // Desktop View - Exactly Same as Original
  return (
    <div className="billing-container">
      <BillingSection title="Battery Subscription Cost">
        <BillingRow>
          <BillingTile title="Total Distance" value={data.distance + " KM"} />
          <BillingTile title="Rate Per Km" value={data.perKmCost} />
          <BillingTile
            title="Total Subscription Cost"
            value={"PKR " + data.batterySubCharges}
          />
        </BillingRow>
      </BillingSection>

      <BillingSection title="Battery Swap Cost">
        <BillingRow>
          <BillingTile title="Total Swaps" value={"" + data.swaps} />
          <BillingTile title="Per Swap Cost" value={data.perSwapCost} />
          <BillingTile
            title="Total Swaps Cost"
            value={"PKR " + data.swapCharges}
          />
        </BillingRow>
      </BillingSection>

      {data.billingItems?.map((dynamicItems: any, index: number) => (
        <BillingSection key={index} title={dynamicItems.item}>
          <BillingRow>
            <BillingTile
              title={dynamicItems.units === "KM" ? "Distance" : "Quantity"}
              value={dynamicItems.quantity + " " + dynamicItems.units}
            />
            <BillingTile
              title="Rate"
              value={
                dynamicItems.rate !== 0 ? "PKR " + dynamicItems.rate : "-"
              }
            />
            <BillingTile title="Total" value={"" + dynamicItems.total} />
          </BillingRow>
        </BillingSection>
      ))}
    </div>
  );
}